'use client'

import useCollectionStats from '@/utils/hooks/useCollectionStats'
import useBidReference from '@/utils/hooks/useBidReference'
import OfferUsd from './offer-usd'
import { TriangleAlert } from 'lucide-react'
import { deepDiscountPercent, floorGap } from '@/utils/offer-floor-gap'
import dialogStyles from './offer-dialog.module.css'
import styles from './offer-usd.module.css'

/**
 * How far an offer sits under the collection's floor price (the fair-value
 * mark, or the listing floor while no mark exists). `loading` is true while
 * the floor is still being read, so an accept can wait for it.
 */
export function useOfferFloorGap(slug: string | null | undefined, priceSats: number) {
	const { data, isLoading } = useCollectionStats(slug || '')
	const floor = Number(data?.floor_price)
	const ref = useBidReference(slug || null, Number.isFinite(floor) && floor > 0 ? floor : null)
	const gap = floorGap(priceSats, ref.sats)
	return { gap, label: ref.label, loading: !gap && !!slug && isLoading }
}

/** How an offer compares to the collection's fair-value mark (floor when unpriced). */
export default function OfferFloor({ slug, priceSats, warningCard = false }: { slug: string; priceSats: number; warningCard?: boolean }) {
	const { data, isLoading } = useCollectionStats(slug)
	const floor = Number(data?.floor_price)
	const ref = useBidReference(slug, Number.isFinite(floor) && floor > 0 ? floor : null)
	const anchor = ref.sats
	if (!anchor && warningCard) return null
	const gap = floorGap(priceSats, anchor)
	// A deep discount is a different message: red, and the first thing read.
	if (warningCard && anchor && gap?.deep)
		return (
			<div className={`${dialogStyles.saleWarning} ${dialogStyles.saleDanger}`} role="alert" data-testid="offer-deep-discount">
				<TriangleAlert size={18} aria-hidden="true" />
				<div>
					<strong>This offer is {deepDiscountPercent(gap)}% below the floor price</strong>
					<span>
						{ref.label}: {(anchor / 1e8).toLocaleString('en-US', { maximumFractionDigits: 8 })} BTC{' '}
						<OfferUsd sats={anchor} />. Check the amount before you sell.
					</span>
				</div>
			</div>
		)
	if (!anchor)
		return (
			<span className={styles.floor}>
				{isLoading ? 'Loading collection value…' : 'Collection value unavailable'}
			</span>
		)
	const label = 'floor price'
	const percent = Math.abs((priceSats / anchor - 1) * 100)
	// Ten percent or more under the mark is a sale a seller should notice.
	const low = priceSats < anchor && percent >= 10
	const difference =
		percent < 0.1 && priceSats !== anchor
			? '<0.1'
			: percent.toLocaleString('en-US', { maximumFractionDigits: 1 })
	if (warningCard) return <div className={dialogStyles.saleWarning}><TriangleAlert size={15} aria-hidden="true" /><div><span>{ref.label}: {(anchor / 1e8).toLocaleString('en-US', { maximumFractionDigits: 8 })} BTC <OfferUsd sats={anchor} /></span><span>{priceSats === anchor ? `Offer is at ${label}` : <>Offer is <span className={dialogStyles.warningPercent}>{difference}%</span> {priceSats < anchor ? 'below' : 'above'} {label}</>}</span></div></div>
	return (
		<div className={`${styles.floor} ${low ? styles.low : ''}`} data-low={low || undefined}>
			<span>
				{ref.label}:{' '}
				<b>{(anchor / 1e8).toLocaleString('en-US', { maximumFractionDigits: 8 })} BTC</b>{' '}
				<OfferUsd sats={anchor} />
			</span>
			<strong>
				{priceSats === anchor
					? `Offer is at ${label}`
					: `Offer is ${difference}% ${priceSats < anchor ? 'below' : 'above'} ${label}`}
			</strong>
		</div>
	)
}
